import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHeart, FiUsers, FiTarget } = FiIcons;

const StorySection = () => {
  return (
    <section className="py-16 px-4 bg-slate-800/30 relative overflow-hidden">
      {/* Background decorative image */}
      <div className="absolute inset-0 opacity-5">
        <img 
          src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751394399737-blob" 
          alt="Background" 
          className="absolute top-0 right-0 w-1/2 h-full object-cover"
        />
      </div>
      
      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              ทำไมเราถึงจัดหลักสูตรนี้?
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-amber-400 to-yellow-600 mx-auto mb-8"></div>
          </div>

          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-amber-500/20"
            >
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiHeart} className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-amber-400 mb-3">
                    จากประสบการณ์จริง
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    เราเห็นคนมากมายที่มีความสามารถ มีความตั้งใจ แต่ก็ยังไม่ประสบความสำเร็จด้านการเงิน 
                    หลายคนบอกว่า "เหมือนมีอะไรมาขัดขวางเสมอ" หรือ "ทำงานหนักแต่เงินไม่เข้า"
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-green-500/20"
            >
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiTarget} className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-green-400 mb-3">
                    การค้นพบที่สำคัญ
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    เมื่อเราเริ่มศึกษาปาจื้อและนำมาใช้ในการวิเคราะห์ด้านการเงิน เราพบว่า 
                    <span className="text-amber-400 font-semibold"> แต่ละคนมีโครงสร้างดวงที่แตกต่างกัน</span> 
                    วิธีเรียกทรัพย์ที่เหมาะสมจึงไม่เหมือนกัน
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-blue-500/20"
            >
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiUsers} className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-blue-400 mb-3">
                    ผลลัพธ์ที่เห็นได้จริง
                  </h3>
                  <p className="text-slate-300 leading-relaxed">
                    หลังจากที่เราช่วยเหลือผู้คนมากมายด้วยความรู้นี้ และเห็นผลลัพธ์ที่ดี เราจึงตัดสินใจจัดหลักสูตรนี้ขึ้น 
                    เพื่อให้คนไทยได้มีโอกาสเรียนรู้และ <span className="text-green-400 font-semibold"> ช่วยเหลือตัวเองได้</span>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mt-12"
          >
            <div className="bg-gradient-to-r from-amber-500/20 to-yellow-600/20 rounded-xl p-8 border border-amber-500/30">
              <h3 className="text-2xl font-bold text-amber-400 mb-4">
                เป้าหมายของเรา
              </h3>
              <p className="text-lg text-white leading-relaxed">
                ไม่ใช่การขาย แต่เป็น<span className="text-amber-400 font-semibold">การแบ่งปันความรู้</span> 
                ที่จะช่วยให้คุณมีชีวิตที่ดีขึ้น มีความมั่นคงทางการเงิน และสามารถเรียกทรัพย์ให้ตัวเองได้อย่างถูกต้อง
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default StorySection;